import { ArrowLeft, Gauge, Pause, Play, RotateCcw, Shield } from 'lucide-react';
import { Application, Container, Graphics, Text, TextStyle } from 'pixi.js';
import { useEffect, useRef, useState } from 'react';
import { playerById } from '../data';
import { matchLogNarration } from '../matchNarration';
import type { MatchResult, WorldFrame } from '../types';

const PRESENTATION_SECONDS = 300;

export function OnlineReplayPage({ result, onExit }: { result: MatchResult; onExit(): void }) {
  const duration = result.frames.at(-1)?.second ?? 5_400;
  const [elapsed, setElapsed] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState(1);
  const matchSecond = Math.min(duration, elapsed / PRESENTATION_SECONDS * duration);
  const finished = matchSecond >= duration;
  const visibleEvents = result.events.filter((event) => event.second <= matchSecond);
  const latestEvent = visibleEvents.at(-1);
  const currentScore = latestEvent ? [latestEvent.homeScore, latestEvent.awayScore] : [0, 0];

  useEffect(() => {
    if (!playing || finished) return;
    let previous = performance.now();
    const interval = window.setInterval(() => {
      const now = performance.now();
      setElapsed((value) => Math.min(PRESENTATION_SECONDS, value + (now - previous) / 1_000 * speed));
      previous = now;
    }, 50);
    return () => window.clearInterval(interval);
  }, [finished, playing, speed]);

  useEffect(() => { if (finished) setPlaying(false); }, [finished]);

  const restart = () => {
    setElapsed(0);
    setPlaying(true);
  };
  const seekTo = (targetSecond: number) => {
    setElapsed(Math.max(0, Math.min(duration, targetSecond)) / duration * PRESENTATION_SECONDS);
  };

  return <div className="match-page online-replay-page">
    <header className="match-header">
      <button className="match-back" onClick={onExit}><ArrowLeft size={18} /> Volver al portal</button>
      <div className="match-competition"><span>LIGA ONLINE · REPLAY AUTORITATIVO</span><small>Mismo resultado persistido para ambos entrenadores</small></div>
      <span className="online-replay-verified"><Shield size={16} /> VERIFICADO</span>
    </header>

    <div className="scoreboard">
      <div className="score-team home"><div className="score-team-copy"><span>LOCAL</span><b>{result.home.name}</b></div><div className="score-team-badge"><Shield size={27} /></div></div>
      <div className="score-center"><span>{formatMinute(matchSecond)}</span><strong>{finished ? result.homeScore : currentScore[0]} <i>—</i> {finished ? result.awayScore : currentScore[1]}</strong><small>{finished ? 'FINAL' : matchSecond >= duration / 2 ? 'SEGUNDA PARTE' : 'PRIMERA PARTE'}</small></div>
      <div className="score-team away"><div className="score-team-badge"><Shield size={27} /></div><div className="score-team-copy"><span>VISITANTE</span><b>{result.away.name}</b></div></div>
    </div>

    <main className="match-workspace">
      <section className="pitch-shell">
        <OnlinePixiPitch result={result} matchSecond={matchSecond} />
        {latestEvent && !finished && <div className={`live-event event-${latestEvent.type.toLowerCase()}`}><small>{formatMinute(latestEvent.second)}</small><b>{matchLogNarration(latestEvent).text}</b><span>{latestEvent.type}</span></div>}
        {finished && <div className="fulltime-overlay"><span>RESULTADO AUTORITATIVO</span><h2>{result.homeScore} <i>—</i> {result.awayScore}</h2><p>Este replay se reproduce desde los datos guardados; el navegador no recalcula el encuentro.</p><div><button className="secondary-action" onClick={restart}><RotateCcw size={16} /> Repetir</button><button className="primary-action" onClick={onExit}>Volver al portal</button></div></div>}
      </section>

      <aside className="match-sidebar">
        <div className="live-label online-replay-label"><i /> REPLAY <span>Seed publicada {result.seed}</span></div>
        <div className="event-feed" aria-label="Cronología del partido online">
          {visibleEvents.slice(-8).reverse().map((event, index) => <div className={`feed-event ${index === 0 ? 'latest' : ''}`} key={`${event.second}-${event.type}-${index}`}><time>{formatClock(event.second)}</time><span><b>{matchLogNarration(event).text}</b><small>{event.type}</small></span></div>)}
          {!visibleEvents.length && <div className="feed-empty">El encuentro está a punto de comenzar.</div>}
        </div>
        <div className="live-stats"><span className="eyebrow">DATOS FINALES</span><ReplayStat label="Posesión" home={result.stats.possession[0]} away={result.stats.possession[1]} suffix="%" /><ReplayStat label="Tiros" home={result.stats.shots[0]} away={result.stats.shots[1]} /><ReplayStat label="A puerta" home={result.stats.shotsOnTarget[0]} away={result.stats.shotsOnTarget[1]} /><ReplayStat label="Pases" home={result.stats.passes[0]} away={result.stats.passes[1]} /></div>
      </aside>
    </main>

    <footer className="match-controls">
      <button onClick={() => setPlaying(!playing)} className="play-control">{playing ? <Pause size={18} /> : <Play size={18} />}</button>
      <span className="control-time">{formatClock(matchSecond)}</span>
      <div className="timeline-track" role="slider" aria-label="Posición del replay" aria-valuemin={0} aria-valuemax={Math.round(duration)} aria-valuenow={Math.round(matchSecond)} tabIndex={0} onClick={(event) => { const rect = event.currentTarget.getBoundingClientRect(); seekTo((event.clientX - rect.left) / rect.width * duration); }} onKeyDown={(event) => { if (event.key === 'ArrowLeft') seekTo(matchSecond - 30); if (event.key === 'ArrowRight') seekTo(matchSecond + 30); if (event.key === 'Home') seekTo(0); if (event.key === 'End') seekTo(duration); }}><i style={{ width: `${matchSecond / duration * 100}%` }} /><b style={{ left: `${matchSecond / duration * 100}%` }} /></div>
      <span className="control-time dim">{formatClock(duration)}</span>
      <div className="speed-control"><Gauge size={16} />{[1, 2, 4].map((value) => <button className={speed === value ? 'active' : ''} onClick={() => setSpeed(value)} key={value}>{value}×</button>)}</div>
    </footer>
  </div>;
}

function OnlinePixiPitch({ result, matchSecond }: { result: MatchResult; matchSecond: number }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef({ result, matchSecond });
  stateRef.current = { result, matchSecond };

  useEffect(() => {
    if (!hostRef.current || !result.frames.length) return;
    const host = hostRef.current;
    let cancelled = false;
    let initialized = false;
    let app: Application | null = null;
    let disconnect: (() => void) | undefined;
    const initialize = async () => {
      app = new Application();
      await app.init({ resizeTo: host, backgroundAlpha: 0, antialias: true, resolution: Math.min(devicePixelRatio, 2), autoDensity: true });
      initialized = true;
      if (cancelled || !app) { app?.destroy(true, { children: true }); return; }
      host.appendChild(app.canvas);
      const field = new Graphics();
      const playerLayer = new Container();
      const trail = new Graphics();
      const ballShadow = new Graphics().circle(0, 0, 14).fill({ color: 0x061018, alpha: .42 });
      const ball = new Graphics().circle(0, 0, 9).fill(0xff9f43).circle(0, 0, 7).fill(0xffffff).circle(2, -2, 2).fill(0xdfe8ef);
      const nodes = new Map<string, Container>();
      const ballHistory: Array<{ x: number; y: number }> = [];
      app.stage.addChild(field, trail, playerLayer, ballShadow, ball);
      const drawField = () => {
        if (!app) return;
        const width = app.screen.width;
        const height = app.screen.height;
        const pad = Math.max(20, Math.min(width, height) * .045);
        const fieldWidth = width - pad * 2;
        const fieldHeight = height - pad * 2;
        field.clear().roundRect(pad, pad, fieldWidth, fieldHeight, 16).fill(0x123f35);
        for (let index = 0; index < 12; index += 2) field.rect(pad + fieldWidth / 12 * index, pad, fieldWidth / 12, fieldHeight).fill({ color: 0x174a3d, alpha: .58 });
        field.roundRect(pad, pad, fieldWidth, fieldHeight, 16).stroke({ color: 0x9bc8b7, width: 2, alpha: .72 });
        field.moveTo(width / 2, pad).lineTo(width / 2, height - pad).stroke({ color: 0x9bc8b7, width: 2, alpha: .72 });
        field.circle(width / 2, height / 2, Math.min(fieldWidth, fieldHeight) * .13).stroke({ color: 0x9bc8b7, width: 2, alpha: .72 });
        field.rect(pad, height * .28, fieldWidth * .15, height * .44).stroke({ color: 0x9bc8b7, width: 2, alpha: .72 });
        field.rect(width - pad - fieldWidth * .15, height * .28, fieldWidth * .15, height * .44).stroke({ color: 0x9bc8b7, width: 2, alpha: .72 });
      };
      result.frames[0].players.forEach((player, index) => {
        const key = `${player.team}:${player.id}:${index}`;
        const node = new Container();
        const shadow = new Graphics().circle(0, 3, 13).fill({ color: 0x000000, alpha: .25 });
        const disc = new Graphics().circle(0, 0, 12).fill(player.team === 0 ? 0x20a4f3 : 0xff647c).circle(0, 0, 12).stroke({ color: elementColor(player.element), width: 3 });
        const label = new Text({ text: shortName(player.id), style: new TextStyle({ fontFamily: 'Inter, Arial', fontSize: 9, fontWeight: '700', fill: 0xffffff }) });
        label.anchor.set(.5);
        node.addChild(shadow, disc, label);
        playerLayer.addChild(node);
        nodes.set(key, node);
      });
      drawField();
      const resizeObserver = new ResizeObserver(drawField);
      resizeObserver.observe(host);
      disconnect = () => resizeObserver.disconnect();
      app.ticker.add(() => {
        if (!app) return;
        const frame = frameAt(stateRef.current.result.frames, stateRef.current.matchSecond);
        const width = app.screen.width;
        const height = app.screen.height;
        const pad = Math.max(20, Math.min(width, height) * .045);
        frame.players.forEach((player, index) => {
          const node = nodes.get(`${player.team}:${player.id}:${index}`);
          if (!node) return;
          const x = pad + player.x / 100 * (width - pad * 2);
          const y = pad + player.y / 100 * (height - pad * 2);
          node.x += (x - node.x) * .2;
          node.y += (y - node.y) * .2;
        });
        const ballX = pad + frame.ball.x / 100 * (width - pad * 2);
        const ballY = pad + frame.ball.y / 100 * (height - pad * 2);
        ball.x += (ballX - ball.x) * .3;
        ball.y += (ballY - ball.y) * .3;
        ballShadow.x += (ballX - ballShadow.x) * .25;
        ballShadow.y += (ballY + 3 - ballShadow.y) * .25;
        ballHistory.push({ x: ball.x, y: ball.y });
        if (ballHistory.length > 8) ballHistory.shift();
        trail.clear();
        ballHistory.forEach((point, index) => trail.circle(point.x, point.y, 2 + index * .22).fill({ color: 0xffb45e, alpha: index / 28 }));
      });
    };
    void initialize();
    return () => {
      cancelled = true;
      disconnect?.();
      if (initialized) app?.destroy(true, { children: true });
    };
  }, [result]);
  return <div ref={hostRef} className="pixi-pitch" />;
}

function frameAt(frames: WorldFrame[], second: number): WorldFrame {
  let lower = 0;
  let upper = frames.length - 1;
  while (lower < upper) {
    const middle = Math.ceil((lower + upper) / 2);
    if (frames[middle].second <= second) lower = middle;
    else upper = middle - 1;
  }
  const current = frames[lower];
  const next = frames[Math.min(frames.length - 1, lower + 1)];
  const amount = next.second === current.second ? 1 : Math.max(0, Math.min(1, (second - current.second) / (next.second - current.second)));
  const players = current.players.map((player, index) => ({ ...player,
    x: lerp(player.x, next.players[index]?.x ?? player.x, amount),
    y: lerp(player.y, next.players[index]?.y ?? player.y, amount),
  }));
  return { ...current, players, ball: { ...current.ball,
    x: lerp(current.ball.x, next.ball.x, amount), y: lerp(current.ball.y, next.ball.y, amount),
  } };
}

function ReplayStat({ label, home, away, suffix = '' }: { label: string; home: number; away: number; suffix?: string }) {
  const total = home + away || 1;
  return <div className="live-stat"><span><b>{home}{suffix}</b><small>{label}</small><b>{away}{suffix}</b></span><div><i style={{ width: `${home / total * 100}%` }} /><em style={{ width: `${away / total * 100}%` }} /></div></div>;
}

const lerp = (from: number, to: number, amount: number) => from + (to - from) * amount;
const formatMinute = (seconds: number) => `${Math.min(90, Math.floor(seconds / 60) + 1)}'`;
const formatClock = (seconds: number) => `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
const shortName = (id: string) => playerById.get(id)?.name.split(' ').map((part) => part[0]).join('').slice(0, 2).toUpperCase() ?? '?';
const elementColor = (element: string) => ({ FIRE: 0xff7849, WIND: 0x58c7ff, FOREST: 0x62d69b, MOUNTAIN: 0xe4b45f }[element] ?? 0xffffff);
